import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ExpandLess,
  ExpandMore,
  EmojiEvents,
  Leaderboard,
  Save,
  Settings,
  Timer,
  Tune,
} from "@mui/icons-material";
import { ip_back } from "../config";
import { TE_STATE_FINALIZED, TE_STATE_STARTED } from "../utils/coachExerciseNavigation";
import { useKotCoachRound } from "../hooks/useKotCoachRound";
import { useKotCoachSubmitResult } from "../hooks/useKotCoachSubmitResult";
import { kotTableDisplayRows } from "../utils/kotCoachRound";
import {
  canCoachEnterResultForMatch,
  canPlayerEnterResultForMatch,
  coachResultEntryBlockedHint,
} from "../utils/kotCoachSubmit";
import { KotCoachStartRoundBanner, KotCoachStopRoundButton } from "./KotCoachStartRoundBanner";
import {
  KotCoachResultDialog,
  KotMatchSubmittedIcon,
} from "./KotCoachResultDialog";
import { KotPlayerResultDialog } from "./KotPlayerResultDialog";
import { KotPlayerMatchesDialog } from "./KotPlayerMatchesDialog";
import "./KotSettings.css";

const KOT_MODES = ["STOP_SCORE_LEAD", "FULL_SET"];
const KOT_TIE_BREAK_RULES = ["GOLDEN_POINT", "TWO_POINT_DIFFERENCE"];
const KOT_POINTS_SCHEMES = [
  "HYBRID",
  "INVERSE_RANK_ELO",
  "TABLE_MULTIPLIER",
  "KING_STREAK",
];

const DEFAULT_POINTS_PARAMS = {
  k_factor: 25,
  base_win_points: 10,
  dethrone_bonus: 20,
  king_streak_bonus: 5,
  golden_point_bonus: 3,
  margin_bonus_enabled: true,
};

const DEFAULT_CONFIG = {
  kot_mode: "STOP_SCORE_LEAD",
  tie_break_rule: "GOLDEN_POINT",
  stop_deadline_ranking_enabled: true,
  set_points_target: 11,
  set_win_by: 2,
  max_rounds: null,
  stop_deadline_seconds: 30,
  round_duration_seconds: null,
  points_scheme: "HYBRID",
  points_params: { ...DEFAULT_POINTS_PARAMS },
};

function authHeaders() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token
    ? { Authorization: token, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function formatElapsed(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "0:00";
  const s = Math.max(0, Math.floor(Number(seconds)));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

/** Completed rounds: current round in progress counts as not yet played. */
export function deriveKotRoundsPlayed(snapshot) {
  const current = snapshot?.current_round;
  const cfgNum = Number(snapshot?.config?.current_round_number) || 0;
  if (!current) return Math.max(0, cfgNum);
  const n = Number(current.round_number) || 0;
  if (current.round_state === "COMPLETED") return n;
  if (
    current.round_state === "IN_PROGRESS" ||
    current.round_state === "STOP_REQUESTED"
  ) {
    return Math.max(0, n - 1);
  }
  if (current.round_state === "PENDING") {
    return n;
  }
  return Math.max(0, n - 1);
}

function normalizeKotMode(mode) {
  if (mode === "FULL_SET") return "FULL_SET";
  if (mode === "STOP_SCORE_LEAD" || mode === "STOP_ON_FIRST") return "STOP_SCORE_LEAD";
  return "STOP_SCORE_LEAD";
}

function modeSummary(mode, t) {
  const m = normalizeKotMode(mode);
  if (m === "FULL_SET") return t("kotModeFullSet");
  if (m === "STOP_SCORE_LEAD") return t("kotModeStopScoreLead");
  return mode || "—";
}

function tieBreakSummary(rule, t) {
  if (rule === "TWO_POINT_DIFFERENCE") return t("kotTieBreakTwoPointDifference");
  return t("kotTieBreakGoldenPoint");
}

function formatKotPlayerName(row) {
  if (!row) return "—";
  return (
    row.user_alias ||
    row.user_first_name ||
    (row.player_id ? String(row.player_id).slice(-6) : "—")
  );
}

function formatMatchPlayerName(player, playerId, leaderboard) {
  if (player) return formatKotPlayerName(player);
  if (playerId && Array.isArray(leaderboard)) {
    const row = leaderboard.find(
      (r) => String(r.player_id) === String(playerId)
    );
    if (row) return formatKotPlayerName(row);
  }
  if (playerId) return String(playerId).slice(-6);
  return "—";
}

function matchStateLabel(state, t) {
  if (state === "WON") return t("kotMatchWon") || "Decided";
  if (state === "IN_PROGRESS") return t("kotMatchInProgress") || "Playing";
  if (state === "SCHEDULED") return t("kotMatchScheduled") || "Scheduled";
  if (state === "ABANDONED") return t("kotMatchAbandoned") || "Abandoned";
  return state || "—";
}

/**
 * Planning-page panel for one King-of-the-Table exercise.
 * Embedded under the exercise card: exercise info first, match settings below.
 */
export function KingOfTableSettings({
  te_id,
  ts_id,
  initialConfig,
  canEdit = true,
  canEnterMatchResults = false,
  canEnterOwnMatchResults = false,
  isCoachView = true,
  viewerPlayerId = null,
  readOnlyView = false,
  onSaved,
  socket = null,
  isActive = false,
  teStartTime = null,
  teEndTime = null,
  plannedDurationMinutes = null,
  remainingSeconds = null,
  embedded = true,
  exerciseStateId = null,
}) {
  const { t } = useTranslation();
  const [scoreboardExpanded, setScoreboardExpanded] = useState(false);
  const [resultsExpanded, setResultsExpanded] = useState(false);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tick, setTick] = useState(0);
  const [historyPlayer, setHistoryPlayer] = useState(null);

  const resolvedExerciseStateId =
    exerciseStateId ?? (isActive ? TE_STATE_STARTED : null);

  const coachRound = useKotCoachRound({
    socket,
    teId: te_id,
    tsId: ts_id,
    exerciseStateId: resolvedExerciseStateId,
    enabled: Boolean(socket && ts_id && te_id),
    stopRoundMode: isCoachView ? "coach" : "player",
    viewerPlayerId: isCoachView ? null : viewerPlayerId,
  });
  const snapshot = coachRound.snapshot;

  const resultSubmit = useKotCoachSubmitResult({
    socket,
    tsId: ts_id,
    teId: te_id,
    snapshot,
    enabled: Boolean(
      (canEnterMatchResults || canEnterOwnMatchResults) &&
        socket &&
        ts_id &&
        te_id
    ),
    submitterMode: isCoachView ? "coach" : "player",
    viewerPlayerId: isCoachView ? null : viewerPlayerId,
    onSubmitted: () => coachRound.requestSnapshot({ httpFallback: true }),
  });

  const merged = useMemo(() => {
    const base = initialConfig
      ? {
          ...DEFAULT_CONFIG,
          ...initialConfig,
          kot_mode: normalizeKotMode(initialConfig.kot_mode),
          tie_break_rule:
            initialConfig.tie_break_rule || DEFAULT_CONFIG.tie_break_rule,
          stop_deadline_ranking_enabled:
            initialConfig.stop_deadline_ranking_enabled !== false,
          points_params: {
            ...DEFAULT_POINTS_PARAMS,
            ...(initialConfig.points_params || {}),
          },
        }
      : DEFAULT_CONFIG;
    return base;
  }, [initialConfig]);

  const [form, setForm] = useState(merged);
  useEffect(() => {
    setForm(merged);
  }, [merged]);

  useEffect(() => {
    if (!isActive) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  const roundsPlayed = useMemo(
    () => deriveKotRoundsPlayed(snapshot),
    [snapshot]
  );

  const maxRounds =
    snapshot?.config?.max_rounds ?? form.max_rounds ?? null;

  const elapsedSeconds = useMemo(() => {
    void tick;
    if (teEndTime) {
      const end = new Date(teEndTime).getTime();
      const start = teStartTime ? new Date(teStartTime).getTime() : null;
      if (start && !Number.isNaN(start) && !Number.isNaN(end)) {
        return Math.max(0, Math.floor((end - start) / 1000));
      }
    }
    if (isActive && teStartTime) {
      const start = new Date(teStartTime).getTime();
      if (!Number.isNaN(start)) {
        return Math.max(0, Math.floor((Date.now() - start) / 1000));
      }
    }
    if (
      isActive &&
      remainingSeconds != null &&
      Number(plannedDurationMinutes) > 0
    ) {
      const total = Number(plannedDurationMinutes) * 60;
      return Math.max(0, total - Math.max(0, Number(remainingSeconds)));
    }
    return 0;
  }, [
    tick,
    isActive,
    teStartTime,
    teEndTime,
    remainingSeconds,
    plannedDurationMinutes,
  ]);

  const currentRoundNumber = snapshot?.current_round?.round_number ?? null;
  const currentRoundState = snapshot?.current_round?.round_state ?? null;
  const leaderboard = Array.isArray(snapshot?.leaderboard)
    ? snapshot.leaderboard
    : [];
  const roundMatches = useMemo(
    () => kotTableDisplayRows(snapshot),
    [snapshot]
  );

  const kotConfigForEntry = useMemo(
    () => snapshot?.config || form,
    [snapshot?.config, form]
  );

  const roundDurationMinutes = useMemo(() => {
    const sec =
      form.round_duration_seconds ??
      snapshot?.round_timer?.round_duration_seconds ??
      null;
    if (sec == null || sec === "") return null;
    return Math.max(1, Math.round(Number(sec) / 60));
  }, [form.round_duration_seconds, snapshot?.round_timer?.round_duration_seconds]);

  const isFullSet = form.kot_mode === "FULL_SET";
  const isStopScoreLead = form.kot_mode === "STOP_SCORE_LEAD";
  const isFinalized = resolvedExerciseStateId === TE_STATE_FINALIZED;
  const showLive = isActive;
  const showFinalizedResults = isFinalized && !isActive;
  const showScoreboardSection =
    showLive || showFinalizedResults || (readOnlyView && leaderboard.length > 0);
  const showMatchSettingsSection = canEdit;
  const scoreboardToggleExpanded = showFinalizedResults
    ? resultsExpanded
    : scoreboardExpanded;
  const setScoreboardToggleExpanded = showFinalizedResults
    ? setResultsExpanded
    : setScoreboardExpanded;

  useEffect(() => {
    if (isActive) setScoreboardExpanded(true);
  }, [isActive]);

  useEffect(() => {
    if (showFinalizedResults) setResultsExpanded(true);
  }, [showFinalizedResults]);

  useEffect(() => {
    setResultsExpanded(false);
  }, [te_id]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const setParam = (key, value) =>
    setForm((prev) => ({
      ...prev,
      points_params: { ...(prev.points_params || {}), [key]: value },
    }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body = {
        te_id,
        ts_id,
        kot_mode: form.kot_mode,
        tie_break_rule: form.tie_break_rule,
        stop_deadline_ranking_enabled: form.stop_deadline_ranking_enabled !== false,
        set_points_target: Math.max(3, Number(form.set_points_target) || 11),
        set_win_by: Math.max(1, Number(form.set_win_by) || 2),
        max_rounds:
          form.max_rounds == null || form.max_rounds === ""
            ? null
            : Math.max(1, Number(form.max_rounds)),
        stop_deadline_seconds: Math.max(
          5,
          Number(form.stop_deadline_seconds) || 30
        ),
        round_duration_seconds:
          form.round_duration_seconds == null ||
          form.round_duration_seconds === ""
            ? null
            : Math.max(60, Number(form.round_duration_seconds)),
        points_scheme: KOT_POINTS_SCHEMES.includes(form.points_scheme)
          ? form.points_scheme
          : "HYBRID",
        points_params: {
          k_factor: Math.max(0, Number(form.points_params?.k_factor) || 0),
          base_win_points: Math.max(
            0,
            Number(form.points_params?.base_win_points) || 0
          ),
          dethrone_bonus: Math.max(
            0,
            Number(form.points_params?.dethrone_bonus) || 0
          ),
          king_streak_bonus: Math.max(
            0,
            Number(form.points_params?.king_streak_bonus) || 0
          ),
          golden_point_bonus: Math.max(
            0,
            Number(form.points_params?.golden_point_bonus) || 0
          ),
          margin_bonus_enabled: !!form.points_params?.margin_bonus_enabled,
        },
      };
      const resp = await fetch(`${ip_back}api/kot/config`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json.status !== "success") {
        throw new Error(json.message || `HTTP ${resp.status}`);
      }
      setSuccess(true);
      if (typeof onSaved === "function") onSaved(json.data?.config || null);
      setTimeout(() => setSuccess(false), 2500);
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const roundsLabel =
    maxRounds != null && maxRounds !== ""
      ? `${roundsPlayed} / ${maxRounds}`
      : String(roundsPlayed);

  const ruleChips = (
    <div className="kot-settings__chips">
      <span className="kot-settings__chip">{modeSummary(form.kot_mode, t)}</span>
      {isStopScoreLead ? (
        <span className="kot-settings__chip">
          {tieBreakSummary(form.tie_break_rule, t)}
        </span>
      ) : null}
      {isFullSet ? (
        <span className="kot-settings__chip">
          {form.set_points_target} · {t("kotSetWinBy")} {form.set_win_by}
        </span>
      ) : null}
      {isStopScoreLead ? (
        <span className="kot-settings__chip">
          {form.stop_deadline_seconds}s stop
        </span>
      ) : null}
      {maxRounds != null && maxRounds !== "" ? (
        <span className="kot-settings__chip">
          {t("kotMaxRounds")} {maxRounds}
        </span>
      ) : null}
    </div>
  );

  const rootClass = embedded
    ? "kot-settings kot-settings--embedded"
    : "kot-settings";

  const openPlayerHistory = (row, e) => {
    e?.stopPropagation?.();
    if (!row?.player_id) return;
    setHistoryPlayer(row);
  };

  const leaderboardTable =
    leaderboard.length === 0 ? (
      <p className="kot-settings__hint">
        {t("kotLeaderboardEmpty") ||
          "Leaderboard will appear after the first round."}
      </p>
    ) : (
      <div className="kot-settings__table-wrap">
        <table className="kot-settings__table">
          <thead>
            <tr>
              <th style={thStyle()}>#</th>
              <th style={thStyle()}>{t("player") || "Player"}</th>
              <th style={thStyle()}>{t("table") || "Table"}</th>
              <th style={thStyle()}>{t("points") || "Pts"}</th>
              <th style={thStyle()}>{t("won") || "W"}</th>
              <th style={thStyle()}>{t("lost") || "L"}</th>
            </tr>
          </thead>
          <tbody>
            {leaderboard.slice(0, 12).map((row, idx) => {
              const isSelf =
                viewerPlayerId &&
                row?.player_id &&
                String(row.player_id) === String(viewerPlayerId);
              return (
              <tr
                key={row.kot_score_id || row.player_id || idx}
                className={isSelf ? "kot-settings__row--self" : undefined}
              >
                <td style={tdStyle()}>{idx + 1}</td>
                <td style={tdStyle()}>
                  <button
                    type="button"
                    className="kot-settings__player-link"
                    onClick={(e) => openPlayerHistory(row, e)}
                    title={
                      t("kotViewMatchHistory") || "View match history"
                    }
                  >
                    {formatKotPlayerName(row)}
                    {isSelf ? (
                      <span className="kot-settings__you-badge">
                        {t("kotYou") || "You"}
                      </span>
                    ) : null}
                  </button>
                </td>
                <td style={tdStyle()}>{row.current_table_number ?? "—"}</td>
                <td style={{ ...tdStyle(), fontWeight: 600 }}>
                  {row.total_points ?? 0}
                </td>
                <td style={tdStyle()}>{row.matches_won ?? 0}</td>
                <td style={tdStyle()}>{row.matches_lost ?? 0}</td>
              </tr>
            );
            })}
          </tbody>
        </table>
        <p className="kot-settings__hint kot-settings__hint--inline">
          {t("kotViewMatchHistoryHint") ||
            "Tap a player name to see their matches and results."}
        </p>
      </div>
    );

  return (
    <div className={rootClass} onClick={(e) => e.stopPropagation()}>
      {!embedded ? (
        <div className="kot-settings__title-row">
          <EmojiEvents style={{ fontSize: 18 }} />
          <span>{t("kingOfTheTable")}</span>
        </div>
      ) : null}

      {isActive ? (
        <section className="kot-settings__section kot-settings__section--live">
          <div className="kot-settings__section-head">
            <Timer style={{ fontSize: 17 }} />
            <span>{t("kotRoundsDetails") || "Live round"}</span>
          </div>
          <div className="kot-settings__stats-grid">
            <div style={statBoxStyle()}>
              <span style={statLabelStyle()}>
                {t("kotRoundsPlayed") || "Rounds played"}
              </span>
              <span style={statValueStyle()}>{roundsLabel}</span>
              {currentRoundNumber != null ? (
                <span style={statHintStyle()}>
                  {t("kotCurrentRound") || "Current round"}: {currentRoundNumber}
                  {currentRoundState ? ` (${currentRoundState})` : ""}
                </span>
              ) : null}
            </div>
            <div style={statBoxStyle()}>
              <span style={statLabelStyle()}>
                {t("kotElapsedTime") || "Elapsed time"}
              </span>
              <span style={statValueStyle()}>{formatElapsed(elapsedSeconds)}</span>
            </div>
          </div>
          {ruleChips}
          {coachRound.awaitingCoachStart ||
          coachRound.showSubmissionProgress ||
          coachRound.roundClosing ||
          coachRound.tableDataSyncPending ||
          coachRound.startError ? (
            isCoachView ? (
            <div className="kot-coach-start-slot--exercise-card">
            <KotCoachStartRoundBanner
              compact
              showStartButton={false}
              canStartRound={coachRound.canStartRound}
              awaitingCoachStart={coachRound.awaitingCoachStart}
              preparingNextRound={coachRound.preparingNextRound}
              roundClosing={coachRound.roundClosing}
              tableDataSyncPending={coachRound.tableDataSyncPending}
              showSubmissionProgress={coachRound.showSubmissionProgress}
              submissionSummary={coachRound.submissionSummary}
              roundNumber={coachRound.roundNumber}
              starting={coachRound.startingRound}
              startError={coachRound.startError}
              onStart={coachRound.startNextRound}
              onDismissError={coachRound.clearStartError}
              rotationMoves={coachRound.snapshot?.rotation}
            />
            </div>
            ) : null
          ) : null}
          {!isCoachView && canEnterOwnMatchResults ? (
            <div className="kot-player-actions-inline">
              <KotCoachStopRoundButton
                visible={coachRound.canRequestStopRound}
                stopping={coachRound.stoppingRound}
                onStop={coachRound.requestStopRound}
              />
              {coachRound.stopRoundError ? (
                <p className="kot-settings__match-hint">
                  {t(coachRound.stopRoundError)}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : embedded && !showFinalizedResults ? (
        <div className="kot-settings__preview">{ruleChips}</div>
      ) : null}

      {isActive && roundMatches.length > 0 ? (
        <section className="kot-settings__section">
          <div className="kot-settings__section-head">
            <Leaderboard style={{ fontSize: 17 }} />
            <span>{t("kotTableResults") || "Table results"}</span>
          </div>
          {canEnterMatchResults ? (
            <p className="kot-settings__coach-hint">
              {t("kotCoachEnterResultHint")}
            </p>
          ) : canEnterOwnMatchResults ? (
            <p className="kot-settings__coach-hint">
              {t("kotPlayerEnterResultHint")}
            </p>
          ) : null}
          <div className="kot-settings__matches">
            {roundMatches.map((m) => {
              const winnerIsA =
                m.winner_player_id &&
                String(m.winner_player_id) === String(m.player_a_id);
              const winnerIsB =
                m.winner_player_id &&
                String(m.winner_player_id) === String(m.player_b_id);
              const stateKey = (m.match_state || "scheduled").toLowerCase();
              const canEnterThis = canEnterMatchResults
                ? canCoachEnterResultForMatch({
                    match: m,
                    config: kotConfigForEntry,
                    roundState: currentRoundState,
                  })
                : canEnterOwnMatchResults
                  ? canPlayerEnterResultForMatch({
                      match: m,
                      config: kotConfigForEntry,
                      roundState: currentRoundState,
                      viewerPlayerId,
                    })
                  : false;
              const blockedHintKey =
                (canEnterMatchResults || canEnterOwnMatchResults) &&
                m.match_state === "IN_PROGRESS" &&
                !canEnterThis
                  ? coachResultEntryBlockedHint({
                      config: kotConfigForEntry,
                      roundState: currentRoundState,
                    })
                  : null;
              const matchExtraClass = canEnterThis
                ? " kot-settings__match--actionable"
                : blockedHintKey
                  ? " kot-settings__match--blocked"
                  : stateKey === "setup" || stateKey === "scheduled"
                    ? " kot-settings__match--setup"
                    : "";
              const openResult = () => {
                if (canEnterThis) resultSubmit.openForMatch(m);
              };
              return (
                <div
                  key={m.kot_match_id}
                  className={`kot-settings__match kot-settings__match--${stateKey}${matchExtraClass}`}
                  role={canEnterThis ? "button" : undefined}
                  tabIndex={canEnterThis ? 0 : undefined}
                  onClick={canEnterThis ? openResult : undefined}
                  onKeyDown={
                    canEnterThis
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openResult();
                          }
                        }
                      : undefined
                  }
                >
                  <div className="kot-settings__match-head">
                    <strong>
                      {t("table_with_number", {
                        number: m.table_number ?? "—",
                      }) || `Table ${m.table_number ?? "—"}`}
                    </strong>
                    <span className="kot-settings__match-state">
                      {matchStateLabel(m.match_state, t)}
                      {m.match_state === "WON" ? <KotMatchSubmittedIcon /> : null}
                    </span>
                  </div>
                  <div className="kot-settings__match-scores">
                    <div
                      className={
                        winnerIsA
                          ? "kot-settings__match-row kot-settings__match-row--winner"
                          : "kot-settings__match-row"
                      }
                    >
                      <span>
                        {formatMatchPlayerName(
                          m.player_a,
                          m.player_a_id,
                          leaderboard
                        )}
                      </span>
                      <span>{m.player_a_score ?? "—"}</span>
                    </div>
                    <div
                      className={
                        winnerIsB
                          ? "kot-settings__match-row kot-settings__match-row--winner"
                          : "kot-settings__match-row"
                      }
                    >
                      <span>
                        {formatMatchPlayerName(
                          m.player_b,
                          m.player_b_id,
                          leaderboard
                        )}
                      </span>
                      <span>{m.player_b_score ?? "—"}</span>
                    </div>
                  </div>
                  {canEnterThis ? (
                    <button
                      type="button"
                      className="kot-settings__match-cta"
                      onClick={(e) => {
                        e.stopPropagation();
                        openResult();
                      }}
                    >
                      {t(
                        isCoachView
                          ? "kotCoachEnterResult"
                          : "kotPlayerEnterResult"
                      )}
                    </button>
                  ) : null}
                  {blockedHintKey ? (
                    <p className="kot-settings__match-hint">
                      {t(blockedHintKey)}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
          {isCoachView ? (
            <KotCoachResultDialog
              open={Boolean(resultSubmit.activeMatch)}
              match={resultSubmit.activeMatch}
              leaderboard={leaderboard}
              coach={resultSubmit}
              formatPlayerLabel={(player, playerId) =>
                formatMatchPlayerName(player, playerId, leaderboard)
              }
            />
          ) : (
            <KotPlayerResultDialog
              open={Boolean(resultSubmit.activeMatch)}
              match={resultSubmit.activeMatch}
              leaderboard={leaderboard}
              coach={resultSubmit}
              formatPlayerLabel={(player, playerId) =>
                formatMatchPlayerName(player, playerId, leaderboard)
              }
            />
          )}
        </section>
      ) : null}

      {showScoreboardSection ? (
        <section className="kot-settings__section">
          <button
            type="button"
            className="kot-settings__toggle"
            onClick={(e) => {
              e.stopPropagation();
              setScoreboardToggleExpanded((v) => !v);
            }}
          >
            <Leaderboard style={{ fontSize: 17 }} />
            <span>
              {showFinalizedResults
                ? t("kotResults")
                : t("kotScoreboard")}
            </span>
            {scoreboardToggleExpanded ? (
              <ExpandLess className="kot-settings__toggle-icon" />
            ) : (
              <ExpandMore className="kot-settings__toggle-icon" />
            )}
          </button>
          {scoreboardToggleExpanded ? (
            <div className="kot-settings__section-body">
              {showFinalizedResults ? (
                <div className="kot-settings__stats-grid" style={{ marginBottom: 12 }}>
                  <div style={statBoxStyle()}>
                    <span style={statLabelStyle()}>
                      {t("kotRoundsPlayed") || "Rounds played"}
                    </span>
                    <span style={statValueStyle()}>{roundsLabel}</span>
                  </div>
                  <div style={statBoxStyle()}>
                    <span style={statLabelStyle()}>
                      {t("kotElapsedTime") || "Elapsed time"}
                    </span>
                    <span style={statValueStyle()}>
                      {formatElapsed(elapsedSeconds)}
                    </span>
                  </div>
                </div>
              ) : null}
              {leaderboardTable}
            </div>
          ) : (
            <p className="kot-settings__hint">
              {showFinalizedResults
                ? `${t("kotRoundsPlayed") || "Rounds played"}: ${roundsLabel}${
                    leaderboard.length > 0
                      ? ` · ${leaderboard.length} ${t("players") || "players"}`
                      : ""
                  } · ${t("tapToExpand") || "tap to expand"}`
                : leaderboard.length > 0
                  ? `${leaderboard.length} ${t("players") || "players"} · ${t("tapToExpand") || "tap to expand"}`
                  : t("kotLeaderboardEmpty") ||
                    "Leaderboard will appear after the first round."}
            </p>
          )}
        </section>
      ) : null}

      {!embedded && !isActive ? (
        <section className="kot-settings__section">
          <div className="kot-settings__section-head">
            <Timer style={{ fontSize: 17 }} />
            <span>{t("kotRoundsDetails") || "Rounds"}</span>
          </div>
          <div className="kot-settings__stats-grid">
            <div style={statBoxStyle()}>
              <span style={statLabelStyle()}>
                {t("kotRoundsPlayed") || "Rounds played"}
              </span>
              <span style={statValueStyle()}>{roundsLabel}</span>
            </div>
            <div style={statBoxStyle()}>
              <span style={statLabelStyle()}>
                {t("kotElapsedTime") || "Elapsed time"}
              </span>
              <span style={statValueStyle()}>{formatElapsed(elapsedSeconds)}</span>
            </div>
          </div>
          <div className="kot-settings__rules-text">{ruleChips}</div>
        </section>
      ) : null}

      {showMatchSettingsSection ? (
      <section className="kot-settings__section kot-settings__section--settings">
        <button
          type="button"
          className="kot-settings__toggle"
          onClick={(e) => {
            e.stopPropagation();
            setSettingsExpanded((v) => !v);
          }}
        >
          <Settings style={{ fontSize: 17 }} />
          <span>{t("kotSettings") || "Match settings"}</span>
          {settingsExpanded ? (
            <ExpandLess className="kot-settings__toggle-icon" />
          ) : (
            <ExpandMore className="kot-settings__toggle-icon" />
          )}
        </button>

        {!settingsExpanded && embedded ? (
          <p className="kot-settings__hint">
            {t("kotSettingsCollapsedHint") ||
              "Tap to configure match winner, tie-break, and scoring."}
          </p>
        ) : null}

        {!settingsExpanded && !embedded ? (
          <div className="kot-settings__rules-text">{ruleChips}</div>
        ) : null}

        {settingsExpanded ? (
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 10,
              }}
            >
              <label style={fieldStyle()}>
                <span style={labelStyle()}>
                  {t("kotMatchWinnerMode") || "Match winner"}
                </span>
                <select
                  value={form.kot_mode}
                  disabled={!canEdit}
                  onChange={(e) => setField("kot_mode", e.target.value)}
                  style={controlStyle()}
                >
                  {KOT_MODES.map((m) => (
                    <option key={m} value={m}>
                      {m === "STOP_SCORE_LEAD"
                        ? t("kotModeStopScoreLead") ||
                          "Stop on score lead"
                        : t("kotModeFullSet") || "Play full set"}
                    </option>
                  ))}
                </select>
              </label>

              {isStopScoreLead ? (
                <>
                  <label style={fieldStyle()}>
                    <span style={labelStyle()}>
                      {t("kotTieBreakRule") || "Tie-break rule"}
                    </span>
                    <select
                      value={form.tie_break_rule}
                      disabled={!canEdit}
                      onChange={(e) => setField("tie_break_rule", e.target.value)}
                      style={controlStyle()}
                    >
                      {KOT_TIE_BREAK_RULES.map((r) => (
                        <option key={r} value={r}>
                          {r === "GOLDEN_POINT"
                            ? t("kotTieBreakGoldenPoint") || "Golden point"
                            : t("kotTieBreakTwoPointDifference") ||
                              "2-point difference"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label style={{ ...fieldStyle(), flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.stop_deadline_ranking_enabled !== false}
                      disabled={!canEdit}
                      onChange={(e) =>
                        setField("stop_deadline_ranking_enabled", e.target.checked)
                      }
                    />
                    <span style={labelStyle()}>
                      {t("kotStopDeadlineRankingEnabled") ||
                        "Auto-resolve unsubmitted tables on deadline (ranking)"}
                    </span>
                  </label>
                </>
              ) : null}

              {isFullSet ? (
                <>
                  <label style={fieldStyle()}>
                    <span style={labelStyle()}>
                      {t("kotSetPointsTarget") || "Set target (points)"}
                    </span>
                    <input
                      type="number"
                      min={3}
                      disabled={!canEdit}
                      value={form.set_points_target}
                      onChange={(e) =>
                        setField("set_points_target", e.target.value)
                      }
                      style={controlStyle()}
                    />
                  </label>
                  <label style={fieldStyle()}>
                    <span style={labelStyle()}>
                      {t("kotSetWinBy") || "Win by"}
                    </span>
                    <input
                      type="number"
                      min={1}
                      disabled={!canEdit}
                      value={form.set_win_by}
                      onChange={(e) => setField("set_win_by", e.target.value)}
                      style={controlStyle()}
                    />
                  </label>
                </>
              ) : null}

              {isStopScoreLead ? (
                <label style={fieldStyle()}>
                  <span style={labelStyle()}>
                    {t("kotStopDeadlineSeconds") || "Stop deadline (sec)"}
                  </span>
                  <input
                    type="number"
                    min={5}
                    disabled={!canEdit}
                    value={form.stop_deadline_seconds}
                    onChange={(e) =>
                      setField("stop_deadline_seconds", e.target.value)
                    }
                    style={controlStyle()}
                  />
                </label>
              ) : null}

              <label style={fieldStyle()}>
                <span style={labelStyle()}>
                  {t("kotMaxRounds") || "Max rounds (blank = until finalize)"}
                </span>
                <input
                  type="number"
                  min={1}
                  disabled={!canEdit}
                  value={form.max_rounds ?? ""}
                  onChange={(e) =>
                    setField(
                      "max_rounds",
                      e.target.value === "" ? null : e.target.value
                    )
                  }
                  style={controlStyle()}
                />
              </label>

              <label style={fieldStyle()}>
                <span style={labelStyle()}>
                  {t("kotRoundDurationMinutes") ||
                    "Round play time (min, blank = exercise duration)"}
                </span>
                <input
                  type="number"
                  min={1}
                  disabled={!canEdit}
                  value={
                    form.round_duration_seconds != null &&
                    form.round_duration_seconds !== ""
                      ? Math.floor(Number(form.round_duration_seconds) / 60)
                      : ""
                  }
                  onChange={(e) =>
                    setField(
                      "round_duration_seconds",
                      e.target.value === ""
                        ? null
                        : Math.max(1, Number(e.target.value)) * 60
                    )
                  }
                  style={controlStyle()}
                />
              </label>

              <label style={fieldStyle()}>
                <span style={labelStyle()}>
                  {t("kotPointsScheme") || "Points scheme"}
                </span>
                <select
                  value={form.points_scheme}
                  disabled={!canEdit}
                  onChange={(e) => setField("points_scheme", e.target.value)}
                  style={controlStyle()}
                >
                  {KOT_POINTS_SCHEMES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <button
              type="button"
              onClick={() => setAdvanced((v) => !v)}
              style={{
                marginTop: 12,
                background: "transparent",
                border: "none",
                padding: 0,
                color: "var(--brand-primary, #0877BC)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
              }}
            >
              <Tune style={{ fontSize: 16 }} />
              {t("kotAdvanced") || "Advanced points params"}
              {advanced ? (
                <ExpandLess style={{ fontSize: 18 }} />
              ) : (
                <ExpandMore style={{ fontSize: 18 }} />
              )}
            </button>

            {advanced ? (
              <div
                style={{
                  marginTop: 8,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  ["k_factor", "Elo K-factor"],
                  ["base_win_points", "Base win points"],
                  ["dethrone_bonus", "Dethrone bonus"],
                  ["king_streak_bonus", "King streak bonus"],
                  ["golden_point_bonus", "Golden point bonus"],
                ].map(([k, label]) => (
                  <label key={k} style={fieldStyle()}>
                    <span style={labelStyle()}>{label}</span>
                    <input
                      type="number"
                      min={0}
                      disabled={!canEdit}
                      value={
                        form.points_params?.[k] ??
                        DEFAULT_POINTS_PARAMS[k] ??
                        0
                      }
                      onChange={(e) => setParam(k, e.target.value)}
                      style={controlStyle()}
                    />
                  </label>
                ))}
                <label style={{ ...fieldStyle(), justifyContent: "center" }}>
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={!!form.points_params?.margin_bonus_enabled}
                    onChange={(e) =>
                      setParam("margin_bonus_enabled", e.target.checked)
                    }
                  />
                  <span style={{ marginLeft: 6 }}>
                    {t("kotMarginBonus") || "Margin bonus (FULL_SET only)"}
                  </span>
                </label>
              </div>
            ) : null}

            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <button
                type="button"
                disabled={!canEdit || saving}
                onClick={handleSave}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px",
                  background: canEdit
                    ? "var(--brand-primary, #0877BC)"
                    : "#bbb",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  cursor: canEdit && !saving ? "pointer" : "not-allowed",
                  fontWeight: 600,
                }}
              >
                <Save style={{ fontSize: 16 }} />
                {saving ? t("saving") || "Saving…" : t("save") || "Save"}
              </button>
              {success ? (
                <span style={{ color: "#2e7d32", fontSize: 12 }}>
                  {t("saved") || "Saved"}
                </span>
              ) : null}
              {error ? (
                <span style={{ color: "#c62828", fontSize: 12 }}>{error}</span>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>
      ) : null}

      <KotPlayerMatchesDialog
        open={Boolean(historyPlayer)}
        onClose={() => setHistoryPlayer(null)}
        playerRow={historyPlayer}
        teId={te_id}
        tsId={ts_id}
      />
    </div>
  );
}

function statBoxStyle() {
  return {
    background: "#fff",
    borderRadius: 6,
    padding: "8px 10px",
    border: "1px solid #e3edf5",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  };
}

function statLabelStyle() {
  return { fontSize: 11, color: "#666", fontWeight: 500 };
}

function statValueStyle() {
  return {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--brand-primary, #0877BC)",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.2,
  };
}

function statHintStyle() {
  return { fontSize: 10, color: "#888", marginTop: 2 };
}

function fieldStyle() {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 12,
  };
}

function labelStyle() {
  return { color: "#555", fontWeight: 500 };
}

function controlStyle() {
  return {
    padding: "4px 6px",
    border: "1px solid #ccc",
    borderRadius: 4,
    fontSize: 13,
    background: "white",
  };
}

function thStyle() {
  return {
    textAlign: "left",
    padding: "6px 8px",
    fontWeight: 600,
    fontSize: 11,
    color: "#455a64",
    borderBottom: "1px solid #e3edf5",
  };
}

function tdStyle() {
  return {
    padding: "6px 8px",
    borderBottom: "1px solid #f0f4f8",
  };
}

export default KingOfTableSettings;
